import { React, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import FetchModule from '../../../../Share/Network/FetchModule';
import { useLanguage } from '../../../../../Context/LanguageContext';
import { useTheme } from '../../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../../Styles/theme';
import TotalModalWrapper from '../../../../Share/Normal/TotalModalWrapper';

const COOLDOWN_SECONDS = 180;

export default function SubscriptionRetryModal(props) {
  const { memberId, memberName, data, onClickExit, rendering } = props;
  const { t, isEng } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isInProgress, setIsInProgress] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef(null);

  const history = useHistory();

  const storageKey = `subscriptionRetry_${memberId}_${data?.usageId}`;

  function formatCountdown(seconds) {
    const m = String(Math.floor(seconds / 60)).padStart(1, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  }

  function startCountdownFrom(endTime) {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining === 0) {
        clearInterval(intervalRef.current);
        localStorage.removeItem(storageKey);
      }
    }, 1000);
  }

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const endTime = parseInt(saved);
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      if (remaining > 0) {
        setCountdown(remaining);
        startCountdownFrom(endTime);
      } else {
        localStorage.removeItem(storageKey);
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [storageKey]);

  function onClickRequest() {
    if (isInProgress || countdown > 0) return;
    if (!data) return;

    setIsInProgress(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const requestData = {
          merchantUid: data.merchantUid
        };

        const responseData = await fetchModule.postDataVersion2('sales/retry/subscription_admin', 'POST', requestData);

        // console.log('retry: \n', requestData);

        if (responseData.status === 200) {
          const endTime = Date.now() + COOLDOWN_SECONDS * 1000;
          localStorage.setItem(storageKey, String(endTime));
          setCountdown(COOLDOWN_SECONDS);
          startCountdownFrom(endTime);
          alert(t('memberDetail.subscriptionRetryModal.alertRetrySuccess'));
          rendering();
          onClickExit();
        }
        else if (responseData.status === 401) {
          alert(t('memberDetail.alertLoginRetry'));
          history.push('/');
        }
        else {
          alert(responseData.error);
          setIsInProgress(false);
        }
      }
    )();
  }

  return (
    <TotalModalWrapper>
      <ModalView $isDark={isDark} $isEng={isEng}>
        <HeaderRow>
          <HeaderTextGroup>
            <TitleRow>
              <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('memberDetail.subscriptionRetryModal.title')}</Text>
            </TitleRow>
            <SubtitleRow>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>{t('memberDetail.subscriptionRetryModal.subtitle')}</Text>
            </SubtitleRow>
          </HeaderTextGroup>
        </HeaderRow>
        <ContentRow>
          <BodyText>
            <span>{t('memberDetail.subscriptionRetryModal.bodyLine1', { memberName })}</span>
            <br />
            <span>{t('memberDetail.subscriptionRetryModal.bodyLine2', { membershipName: data ? data.memberShipName : '' })}</span>
            <br />
            <span style={{ color: theme.pointRed }}>{t('memberDetail.subscriptionRetryModal.bodyLine3', { reason: data ? data.terminationReason : '' })}</span>
            <br />
            <span>{t('memberDetail.subscriptionRetryModal.bodyLine4')}</span>
            <br />
            <span>{t('memberDetail.subscriptionRetryModal.bodyLine5')}</span>
          </BodyText>
        </ContentRow>
        <FooterRow>
          <ButtonGroup>
            <CancelButton onClick={onClickExit}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('memberDetail.subscriptionRetryModal.btnCancel')}</Text>
            </CancelButton>
            <RequestButton $disabled={countdown > 0} onClick={onClickRequest}>
              <Text fontFamily='NotoSansKR-Bold' fontSize={countdown > 0 ? 11 : 14} color={theme.subBackground}>
                {countdown > 0
                  ? t('memberDetail.subscriptionRetryModal.btnRequestCountdown', { time: formatCountdown(countdown) })
                  : t('memberDetail.subscriptionRetryModal.btnRequest')}
              </Text>
            </RequestButton>
          </ButtonGroup>
        </FooterRow>
      </ModalView>
    </TotalModalWrapper>
  );
}

const ModalView = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 20px;
  gap: 30px;

  box-sizing: border-box;
  width: ${props => props.$isEng ? '613px' : '425px'};

  border-radius: 10px;
  box-shadow: ${props => props.$isDark ? 'none' : '0px 4px 30px rgba(0, 0, 0, 0.1)'};
  background-color: ${props => props.theme.subBackground};
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;
`;

const HeaderTextGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;
`;

const SubtitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SpacerRow = styled.div`
  width: 100%;
  height: 10px;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;
`;

const BodyText = styled.div`
  font-family: NotoSansKR-Medium;
  font-size: 14px;
  line-height: 25px;

  color: ${props => props.theme.text};

  width: 100%;
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const CancelButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 5px 16px;

  width: 62px;
  height: 40px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  transition: all 0.3s ease-in-out;
`;

const RequestButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 5px 16px;

  width: 130px;
  height: 40px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.$disabled ? props.theme.buttonFill : props.theme.subKey};
  opacity: ${props => props.$disabled ? 0.7 : 1};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: ${props => props.$disabled ? 'default' : 'pointer'};
      opacity: ${props => props.$disabled ? 0.7 : 0.9};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const Text = styled.span`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;
  color: ${props => props.color};
  margin: 0;
  padding: 0;
`;
